import stationDataConfigApi from '@/api/config/stationDataConfig'
import { STATION_GROUP_ID } from '@/constant/station'
import { getApiErrorMessage } from '@/helpers/notify'
import type { STATION_DETAIL, UpdateStation } from '@/types/station'
import { irrigationFigures } from './use-station-save'

/**
 * SCRUM-1145 — the station follows its decoder outside the wizard too.
 *
 * In the wizard a decoder moved between Irrigation and Non-Irrigation retypes
 * its station on the Station step (SCRUM-1157). On /config there is no such
 * step: the decoder grid saves and the station kept its old Type until someone
 * noticed. So the decoder save retypes the station itself, straight after.
 *
 * Read-modify-write from the station's own detail. The PUT replaces the whole
 * record, and the list rows do not carry everything it needs — the geo pin
 * among them, which is how SCRUM-1144 wiped pins.
 */

const NON_IRRIGATION_TYPE = 1
const IRRIGATION_TYPE = 2

const normalise = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

/** The station Type a decoder purpose calls for; null when it is not a station purpose. */
export const stationTypeForPurpose = (purpose: unknown): number | null => {
  const key = normalise(purpose)
  if (key === 'irrigation') return IRRIGATION_TYPE
  if (key === 'nonirrigation') return NON_IRRIGATION_TYPE
  return null
}

/**
 * The PUT body for `detail` with only its Type changed.
 *
 * The decoder comes from `decoder`, not the detail: the detail endpoint does
 * not return `address` or `index`, and a PUT without them is refused.
 */
export const buildRetypePayload = (
  detail: STATION_DETAIL & Record<string, any>,
  {
    interfaceId,
    stationType,
    decoder,
  }: {
    interfaceId: number
    stationType: number
    decoder: {
      id?: string
      address?: number | string
      index?: number | string
      twiId?: number | null
    }
  },
): UpdateStation => {
  const isNonIrrigation = stationType === NON_IRRIGATION_TYPE
  const idOrNull = (value: unknown) => {
    const id = Number(value)
    return Number.isFinite(id) && id > 0 ? id : null
  }

  return {
    id: Number(detail.id),
    interfaceId,
    priority: isNonIrrigation ? 0 : Number(detail.priority) || 0,
    pictureUrl: detail.pictureUrl || '',
    pictureFileName: detail.pictureFileName || '',
    grsCoordinatedLatitude: detail.grsCoordinatedLatitude ?? null,
    grsCoordinatedLongitude: detail.grsCoordinatedLongitude ?? null,
    floorId: detail.floorId ?? detail.floor?.id ?? null,
    ...irrigationFigures({ ...detail, stationType }),
    description: detail.description,
    decoderId: detail.decoderId || decoder.id || undefined,
    index: Number(decoder.index),
    address: Number(decoder.address),
    // Kept as they are, as the station grid's save keeps them: a station moved
    // to non-irrigation hides them rather than losing them (SCRUM-1157).
    plantId: idOrNull(detail.plantId ?? detail.plant?.id),
    soilId: idOrNull(detail.soilId ?? detail.soil?.id),
    emitterId: idOrNull(detail.emitterId ?? detail.emitter?.id),
    pipeId: isNonIrrigation
      ? (null as any)
      : idOrNull(detail.pipeId ?? detail.pipe?.id) ?? 0,
    etZoneId: idOrNull(detail.etZone?.id),
    stationGroupId: STATION_GROUP_ID,
    subAreaId: idOrNull(detail.subArea?.id) as any,
    // The decoder's own twiId: the station list does not always carry one, and
    // 0 is answered with "Controller does not exist".
    twiId: decoder.twiId ?? (detail.controller as any)?.twiId ?? 0,
    Type: stationType,
  }
}

export interface StationRetype {
  /**
   * Gives every station on `decoder` the Type its new purpose calls for.
   * Resolves with the stations that could not be written, each with the
   * server's reason ("Pipe does not exist." for an irrigation station with
   * no pipe).
   */
  apply: (
    decoder: {
      id?: string
      address?: number | string
      index?: number | string
      twiId?: number | null
    },
    purposeName: string,
  ) => Promise<string[]>
}

export const isStationOnDecoder = (
  station: {
    decoderId?: string | null
    address?: number | string
    index?: number | string
    twiId?: number | string | null
  },
  decoder: {
    id?: string
    address?: number | string
    index?: number | string
    twiId?: number | null
  },
) => {
  if (station.decoderId && decoder.id) return station.decoderId === decoder.id
  if (`${station.address}-${station.index}` !== `${decoder.address}-${decoder.index}`)
    return false
  return (
    station.twiId == null ||
    decoder.twiId == null ||
    Number(station.twiId) === Number(decoder.twiId)
  )
}

/** One per save pass: the station list is read at most once. */
export const createStationRetype = (interfaceId: number): StationRetype => {
  let stations: Promise<any[]> | null = null

  const loadStations = () => {
    if (!stations) {
      stations = stationDataConfigApi
        .getAll({ interfaceId, PageSize: 0, PageIndex: 1 } as any)
        .then((res: any) => res?.data?.data ?? [])
      stations.catch(() => {
        stations = null
      })
    }
    return stations
  }

  const apply: StationRetype['apply'] = async (decoder, purposeName) => {
    const stationType = stationTypeForPurpose(purposeName)
    if (!stationType) return []

    const key = `${decoder.address}-${decoder.index}`
    const onDecoder = (await loadStations()).filter(
      (station) =>
        isStationOnDecoder(station, decoder) &&
        Number(station.stationType) !== stationType,
    )

    const failed: string[] = []
    // One at a time, like every other station write here: the endpoint answers
    // 500 under concurrent writes.
    for (const station of onDecoder) {
      try {
        const { data: detail } = await stationDataConfigApi.getById(
          String(interfaceId),
          String(station.id),
        )
        await stationDataConfigApi.update(
          buildRetypePayload({ ...detail, description: detail?.description || station.description } as any, {
            interfaceId,
            stationType,
            decoder,
          }),
        )
        station.stationType = stationType
      } catch (error) {
        console.error('Station retype after decoder purpose change failed:', error)
        failed.push(
          `${station.description || key} (${getApiErrorMessage(error)})`,
        )
      }
    }
    return failed
  }

  return { apply }
}
